import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Snackbar,
  Skeleton,
  Alert,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme as useMuiTheme,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircleOutline as FinalizeIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  CheckCircle as AtivasIcon,
  Cancel as InativasIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  AttachMoney as SalaryIcon,
  DateRange as DateIcon,
} from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";
import {
  buscarVagasEmpresa,
  excluirVaga,
  alterarStatusVaga,
  atualizarVaga,
  finalizarVaga,
} from "../../services/vaga";
import { Vaga } from "../../types/vaga/vaga";
import { formatarData as formatarDataBackend } from "../../utils/data";
import { obterIdEmpresa } from "../../utils/empresa";
import { useNavigate } from "react-router-dom";

const GerenciarVagas: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  
  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Estados para paginação e filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtroStatus, setFiltroStatus] = useState<"ativas" | "inativas">("ativas");
  const [todasVagas, setTodasVagas] = useState<Vaga[]>([]);
  
  // Estados para dialogs
  const [dialogAberto, setDialogAberto] = useState(false);
  const [vagaSelecionada, setVagaSelecionada] = useState<Vaga | null>(null);
  const [tipoAcao, setTipoAcao] = useState<
    "excluir" | "status" | "editar" | "finalizar"
  >("excluir");
  const [vagaEditando, setVagaEditando] = useState<Partial<Vaga>>({});
  
  // Estados para snackbar
  const [snackbar, setSnackbar] = useState<{
    aberto: boolean;
    mensagem: string;
    tipo: "success" | "error";
  }>({ aberto: false, mensagem: "", tipo: "success" });

  const carregarVagas = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const dados = await buscarVagasEmpresa();
      const vagasOrdenadas = Array.isArray(dados) ? dados.sort((a, b) => Number(b.id) - Number(a.id)) : [];
      
      // Guardar todas as vagas para filtros
      setTodasVagas(vagasOrdenadas);
      
      // Aplicar filtro de status
      aplicarFiltroStatus(vagasOrdenadas, filtroStatus);
    } catch (error) {
      console.error("Erro ao carregar vagas:", error);
      setError("Erro ao carregar vagas. Tente novamente.");
      setVagas([]);
      setTodasVagas([]);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => {
    carregarVagas();
  }, [carregarVagas]);

  useEffect(() => {
    aplicarFiltroStatus(todasVagas, filtroStatus);
  }, [filtroStatus, todasVagas]);

  const aplicarFiltroStatus = (
    vagasParaFiltrar: Vaga[],
    status: "ativas" | "inativas",
  ) => {
    const vagasFiltradas = status === "ativas"
      ? vagasParaFiltrar.filter((vaga) => vaga.ativo)
      : vagasParaFiltrar.filter((vaga) => !vaga.ativo);
    
    setVagas(vagasFiltradas);
    setPage(0); // Resetar página
  };

  const handleChangeFiltroStatus = (novoStatus: "ativas" | "inativas") => {
    setFiltroStatus(novoStatus);
    aplicarFiltroStatus(todasVagas, novoStatus);
  };

  const handleExcluir = (vaga: Vaga) => {
    setVagaSelecionada(vaga);
    setTipoAcao("excluir");
    setDialogAberto(true);
  };

  const handleAlterarStatus = (vaga: Vaga) => {
    setVagaSelecionada(vaga);
    setTipoAcao("status");
    setDialogAberto(true);
  };

  const handleEditar = (vaga: Vaga) => {
    setVagaSelecionada(vaga);
    setVagaEditando({
      nomeVaga: vaga.nomeVaga,
      descricaoVaga: vaga.descricaoVaga,
      cargo: vaga.cargo,
      salario: vaga.salario,
    });
    setTipoAcao("editar");
    setDialogAberto(true);
  };

  const handleFinalizar = (vaga: Vaga) => {
    setVagaSelecionada(vaga);
    setTipoAcao("finalizar");
    setDialogAberto(true);
  };

  const mostrarSnackbar = (mensagem: string, tipo: "success" | "error") => {
    setSnackbar({ aberto: true, mensagem, tipo });
  };

  const fecharSnackbar = () => {
    setSnackbar({ ...snackbar, aberto: false });
  };

  const confirmarAcao = async () => {
    if (!vagaSelecionada) return;

    try {
      if (tipoAcao === "excluir") {
        const resultado = await excluirVaga(vagaSelecionada.id);
        if (resultado.sucesso) {
          // Atualizar todas as vagas
          const vagasAtualizadas = todasVagas.filter((v: Vaga) => v.id !== vagaSelecionada.id);
          setTodasVagas(vagasAtualizadas);
          
          // Reaplicar filtro de status
          aplicarFiltroStatus(vagasAtualizadas, filtroStatus);
          
          mostrarSnackbar("Vaga excluída com sucesso!", "success");
        } else {
          mostrarSnackbar(resultado.mensagem || "Erro ao excluir vaga", "error");
        }
      } else if (tipoAcao === "status") {
        const novoStatus = !vagaSelecionada.ativo;
        const sucesso = await alterarStatusVaga(vagaSelecionada.id, novoStatus);
        if (sucesso) {
          // Atualizar todas as vagas
          const vagasAtualizadas = todasVagas.map((v: Vaga) =>
            v.id === vagaSelecionada.id ? { ...v, ativo: novoStatus } : v,
          );
          setTodasVagas(vagasAtualizadas);
          
          // Reaplicar filtro de status
          aplicarFiltroStatus(vagasAtualizadas, filtroStatus);
          
          mostrarSnackbar(
            `Vaga ${novoStatus ? "ativada" : "desativada"} com sucesso!`,
            "success",
          );
        } else {
          mostrarSnackbar("Erro ao alterar status da vaga", "error");
        }
      } else if (tipoAcao === "editar") {
        const sucesso = await atualizarVaga(vagaSelecionada.id, vagaEditando);
        if (sucesso) {
          // Atualizar todas as vagas
          const vagasAtualizadas = todasVagas.map((v: Vaga) =>
            v.id === vagaSelecionada.id ? { ...v, ...vagaEditando } : v,
          );
          setTodasVagas(vagasAtualizadas);
          
          // Reaplicar filtro de status
          aplicarFiltroStatus(vagasAtualizadas, filtroStatus);
          
          mostrarSnackbar("Vaga atualizada com sucesso!", "success");
        } else {
          mostrarSnackbar("Erro ao atualizar vaga", "error");
        }
      } else if (tipoAcao === "finalizar") {
        const sucesso = await finalizarVaga(vagaSelecionada.id);
        if (sucesso) {
          // Atualizar todas as vagas
          const vagasAtualizadas = todasVagas.map((v: Vaga) =>
            v.id === vagaSelecionada.id ? { ...v, ativo: false } : v,
          );
          setTodasVagas(vagasAtualizadas);
          
          // Reaplicar filtro de status
          aplicarFiltroStatus(vagasAtualizadas, filtroStatus);
          
          mostrarSnackbar("Vaga desativada com sucesso!", "success");
        }
      }
    } catch (error) {
      console.error("Erro ao executar ação:", error);
      mostrarSnackbar("Erro ao executar ação", "error");
    } finally {
      setDialogAberto(false);
      setVagaSelecionada(null);
      setVagaEditando({});
    }
  };

  const filtrarVagas = (vagas: Vaga[]) => {
    return vagas.filter(
      (vaga) =>
        vaga.nomeVaga?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.cargo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vaga.descricaoVaga?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const vagasFiltradas = filtrarVagas(vagas);

  const formatarSalario = (salario: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(salario);
  };

  const formatarData = (data?: string) =>
    formatarDataBackend(data, "Não informado");

  // Sem sessão de empresa (ex.: navegador logado como prefeitura) a lista viria
  // vazia sem explicação: avisar e oferecer o login da empresa
  if (!obterIdEmpresa()) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, mt: 2 }}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/login-empresa?logout=true")}
            >
              Entrar como empresa
            </Button>
          }
        >
          O gerenciamento de vagas é exclusivo para empresas. A sessão atual
          não é de uma empresa (por exemplo, está logada como prefeitura).
          Entre com o CNPJ/CPF e a senha da empresa para ver suas vagas.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Cabeçalho */}
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: 700, mb: 1 }}
      >
        Gerenciar Vagas
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Gerencie suas vagas de emprego - ative, desative, edite ou exclua
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Campo de Busca e Filtros */}
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        {/* Toggle para vagas ativas/inativas */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <ToggleButtonGroup
            value={filtroStatus}
            exclusive
            onChange={(_: any, novoStatus: "ativas" | "inativas") =>
              novoStatus && handleChangeFiltroStatus(novoStatus)
            }
            aria-label="filtro de status das vagas"
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="ativas" aria-label="vagas ativas">
              <AtivasIcon sx={{ mr: 1 }} />
              Vagas Ativas
            </ToggleButton>
            <ToggleButton value="inativas" aria-label="vagas inativas">
              <InativasIcon sx={{ mr: 1 }} />
              Vagas Inativas
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder={
            isMobile
              ? "Buscar..."
              : "Buscar por vaga, cargo ou descrição..."
          }
          value={searchTerm}
          onChange={(e: any) => setSearchTerm(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {searchTerm && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "text.secondary" }}
          >
            Vagas encontradas: {vagasFiltradas.length}
          </Typography>
        )}
      </Paper>

      {/* Lista de Vagas */}
      <Paper elevation={3}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {filtroStatus === "ativas" ? (
              <AtivasIcon sx={{ color: "success.main" }} />
            ) : (
              <InativasIcon sx={{ color: "error.main" }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Vagas {filtroStatus === "ativas" ? "Ativas" : "Inativas"} (
              {vagasFiltradas.length})
            </Typography>
          </Box>
        </Box>

        {vagasFiltradas.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <WorkIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchTerm
                ? "Nenhuma vaga encontrada para a busca"
                : "Nenhuma vaga cadastrada ainda"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {/* Versão Desktop - Tabela */}
            {!isMobile && (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Vaga</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cargo</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Salário</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Data Criação</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: { xs: 100, sm: 140 } }}>
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vagasFiltradas
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((vaga) => (
                        <TableRow key={vaga.id} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip
                                label={vaga.nomeVaga || "Vaga não identificada"}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {vaga.cargo || "Não especificado"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ color: "success.main", fontWeight: 500 }}
                            >
                              {formatarSalario(vaga.salario)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {formatarData(vaga.dataCriacao)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {filtroStatus === "ativas" ? (
                                <>
                                  <Tooltip title="Desativar vaga">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAlterarStatus(vaga)}
                                      sx={{
                                        color: "orange",
                                        "&:hover": {
                                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                                        },
                                      }}
                                    >
                                      <VisibilityOffIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Editar vaga">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditar(vaga)}
                                      sx={{
                                        color: "#2196f3",
                                        "&:hover": {
                                          backgroundColor: "rgba(33, 150, 243, 0.1)",
                                        },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Excluir vaga">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleExcluir(vaga)}
                                      sx={{
                                        color: "#f44336",
                                        "&:hover": {
                                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Finalizar vaga">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleFinalizar(vaga)}
                                      startIcon={<FinalizeIcon sx={{ fontSize: 16 }} />}
                                      sx={{
                                        fontSize: "0.75rem",
                                        px: 2,
                                        py: 0.5,
                                        minWidth: 100,
                                        fontWeight: 600,
                                        background:
                                          "linear-gradient(135deg, #388e3c 0%, #4caf50 50%, #66bb6a 100%)",
                                        "&:hover": {
                                          background:
                                            "linear-gradient(135deg, #2e7d32 0%, #388e3c 50%, #4caf50 100%)",
                                        },
                                        boxShadow: "0 2px 6px rgba(56,142,60,0.4)",
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      Finalizar
                                    </Button>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Reativar vaga">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAlterarStatus(vaga)}
                                      sx={{
                                        color: "green",
                                        "&:hover": {
                                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                                        },
                                      }}
                                    >
                                      <PlayIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Excluir vaga">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleExcluir(vaga)}
                                      sx={{
                                        color: "#f44336",
                                        "&:hover": {
                                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Versão Mobile - Cards */}
            {isMobile && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {vagasFiltradas
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((vaga) => (
                    <Card key={vaga.id} elevation={3}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <WorkIcon sx={{ mr: 1, color: "primary.main" }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {vaga.nomeVaga || "Vaga não identificada"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Cargo:</strong> {vaga.cargo || "Não especificado"}
                          </Typography>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SalaryIcon fontSize="small" sx={{ opacity: 0.7 }} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: "success.main" }}
                            >
                              {formatarSalario(vaga.salario)}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <DateIcon fontSize="small" sx={{ opacity: 0.7 }} />
                            <Typography variant="body2">
                              Criada em: {formatarData(vaga.dataCriacao)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Botões de ação */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid #e0e0e0",
                            flexWrap: "wrap",
                          }}
                        >
                          {filtroStatus === "ativas" ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleAlterarStatus(vaga)}
                                sx={{
                                  color: "orange",
                                  "&:hover": {
                                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                                  },
                                }}
                                title="Desativar vaga"
                              >
                                <VisibilityOffIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => handleEditar(vaga)}
                                sx={{
                                  color: "#2196f3",
                                  "&:hover": {
                                    backgroundColor: "rgba(33, 150, 243, 0.1)",
                                  },
                                }}
                                title="Editar vaga"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => handleExcluir(vaga)}
                                sx={{
                                  color: "#f44336",
                                  "&:hover": {
                                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                                  },
                                }}
                                title="Excluir vaga"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>

                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleFinalizar(vaga)}
                                startIcon={<FinalizeIcon />}
                                sx={{
                                  fontWeight: 600,
                                  background:
                                    "linear-gradient(135deg, #388e3c 0%, #4caf50 50%, #66bb6a 100%)",
                                  "&:hover": {
                                    background:
                                      "linear-gradient(135deg, #2e7d32 0%, #388e3c 50%, #4caf50 100%)",
                                  },
                                  boxShadow: "0 2px 6px rgba(56,142,60,0.4)",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                Finalizar
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => handleAlterarStatus(vaga)}
                                startIcon={<PlayIcon />}
                                sx={{ flex: 1, mr: 1 }}
                              >
                                Reativar
                              </Button>

                              <IconButton
                                onClick={() => handleExcluir(vaga)}
                                sx={{ color: "#f44336", ml: 1 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            )}

            <TablePagination
              component={Paper}
              elevation={1}
              sx={{ mt: 2 }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={vagasFiltradas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e: any, newPage: number) => setPage(newPage)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={isMobile ? "Por página:" : "Linhas por página:"}
              labelDisplayedRows={({ from, to, count }: any) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        )}
      </Paper>

      {/* Dialog de Confirmação/Edição */}
      <Dialog
        open={dialogAberto}
        onClose={() => setDialogAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {tipoAcao === "excluir"
            ? "Confirmar Exclusão"
            : tipoAcao === "status"
            ? "Alterar Status"
            : tipoAcao === "finalizar"
            ? "Finalizar Vaga"
            : "Editar Vaga"}
        </DialogTitle>
        <DialogContent>
          {tipoAcao === "editar" ? (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Nome da Vaga"
                value={vagaEditando.nomeVaga || ""}
                onChange={(e: any) =>
                  setVagaEditando({
                    ...vagaEditando,
                    nomeVaga: e.target.value,
                  })
                }
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Cargo"
                value={vagaEditando.cargo || ""}
                onChange={(e: any) =>
                  setVagaEditando({ ...vagaEditando, cargo: e.target.value })
                }
                margin="normal"
                required
              />

              <TextField
                fullWidth
                label="Salário"
                type="number"
                value={vagaEditando.salario || ""}
                onChange={(e: any) =>
                  setVagaEditando({
                    ...vagaEditando,
                    salario: parseFloat(e.target.value) || 0,
                  })
                }
                margin="normal"
                required
                InputProps={{
                  startAdornment: "R$ ",
                }}
              />

              <TextField
                fullWidth
                label="Descrição da Vaga"
                value={vagaEditando.descricaoVaga || ""}
                onChange={(e: any) =>
                  setVagaEditando({
                    ...vagaEditando,
                    descricaoVaga: e.target.value,
                  })
                }
                margin="normal"
                multiline
                rows={4}
                required
              />
            </Box>
          ) : (
            <DialogContentText>
              {tipoAcao === "excluir" ? (
                <>
                  Tem certeza que deseja excluir a vaga "
                  <strong>{vagaSelecionada?.nomeVaga}</strong>"?
                  <br />
                  Esta ação não pode ser desfeita.
                </>
              ) : tipoAcao === "finalizar" ? (
                <>
                  Tem certeza que deseja finalizar a vaga "
                  <strong>{vagaSelecionada?.nomeVaga}</strong>"?
                  <br />A vaga será desativada e não receberá mais candidaturas.
                </>
              ) : (
                <>
                  Deseja {vagaSelecionada?.ativo ? "desativar" : "ativar"} a
                  vaga "<strong>{vagaSelecionada?.nomeVaga}</strong>"?
                </>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={confirmarAcao}
            color={
              tipoAcao === "excluir"
                ? "error"
                : tipoAcao === "finalizar"
                ? "success"
                : "primary"
            }
            variant="contained"
            disabled={
              tipoAcao === "editar" &&
              (!vagaEditando.nomeVaga ||
                !vagaEditando.cargo ||
                !vagaEditando.descricaoVaga ||
                !vagaEditando.salario)
            }
          >
            {tipoAcao === "excluir"
              ? "Excluir"
              : tipoAcao === "status"
              ? "Confirmar"
              : tipoAcao === "finalizar"
              ? "Finalizar Vaga"
              : "Salvar Alterações"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.aberto}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={fecharSnackbar}
          severity={snackbar.tipo}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GerenciarVagas;
